import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import OnboardingWizard from './OnboardingWizard';
import { QuickExportSection } from './ExportButton';
import { 
  FileText, AlertTriangle, CheckCircle, Users, Shield, 
  Calendar, Eye, ClipboardList, GraduationCap, Search,
  Camera, MessageSquare, Wrench, Truck, FlaskConical,
  BarChart3, DollarSign, Lock, FileSearch, Clipboard,
  Siren, AlertCircle, BookOpen, Brain, Languages,
  Sparkles, HelpCircle, Settings, Bell, TrendingUp,
  Target, Award, Zap, Activity, Plus, ArrowRight,
  Home, ChevronRight, RefreshCw, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activatedPrograms, setActivatedPrograms] = useState([]);

  // Check if user needs onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Check onboarding status from backend (persists across sessions)
        const [onboardingRes, programsRes] = await Promise.all([
          axios.get(`${API_URL}/api/auth/onboarding-status`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/programs/activated/list`, { headers })
        ]);
        
        setActivatedPrograms(programsRes.data.activated_programs || []);
        
        const onboardingStatus = onboardingRes.data;
        const hasCompletedOnboarding = onboardingStatus.onboarding_completed;
        const hasSkippedOnboarding = onboardingStatus.onboarding_skipped || localStorage.getItem('onboarding_skipped');
        const hasPrograms = (programsRes.data.activated_programs || []).length > 0;
        
        // Show onboarding only if: not completed, not skipped, and no programs activated
        if (!hasCompletedOnboarding && !hasSkippedOnboarding && !hasPrograms) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
      }
    };
    checkOnboarding();
  }, []);

  const handleOnboardingComplete = (data) => {
    setShowOnboarding(false);
    toast({ title: 'Welcome!', description: 'Your safety program is ready to use.' });
    // Refresh data
    fetchStats();
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('onboarding_skipped', 'true');
    setShowOnboarding(false);
  };

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const endpoints = [
        { key: 'incidents', url: '/api/incidents' },
        { key: 'nearMisses', url: '/api/near-miss' },
        { key: 'hazards', url: '/api/quick-hazard' },
        { key: 'jhas', url: '/api/jha-builder' },
        { key: 'permits', url: '/api/permits' },
        { key: 'investigations', url: '/api/investigations' },
        { key: 'observations', url: '/api/observations' },
        { key: 'training', url: '/api/training/employees' },
        { key: 'aiScans', url: '/api/ai-hazard-scan/scans?limit=100' },
      ];

      const results = await Promise.allSettled(
        endpoints.map(ep => axios.get(`${API_URL}${ep.url}`, { headers }).catch(() => null))
      );

      const newStats = {};
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value?.data) {
          const data = result.value.data;
          const key = endpoints[idx].key;
          
          // Handle AI scans separately
          if (key === 'aiScans' && data.scans) {
            const scans = data.scans;
            newStats.aiScanTotal = scans.length;
            newStats.aiScanHazards = scans.filter(s => s.hazards_count > 0).length;
            newStats.aiScanClean = scans.length > 0 
              ? Math.round((scans.filter(s => s.hazards_count === 0).length / scans.length) * 100) 
              : 100;
          } else if (Array.isArray(data)) {
            newStats[key] = data.length;
          } else if (data.total !== undefined) {
            newStats[key] = data.total;
          } else {
            const possibleKeys = ['incidents', 'near_misses', 'hazards', 'jhas', 'permits', 'investigations', 'observations', 'employees'];
            for (const pk of possibleKeys) {
              if (data[pk] !== undefined) {
                newStats[key] = Array.isArray(data[pk]) ? data[pk].length : data[pk];
                break;
              }
            }
          }
        }
      });
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Quick Actions - Most common tasks
  const quickActions = [
    { label: 'Report Incident', icon: Siren, path: '/incidents', color: 'bg-red-500', desc: 'Log workplace incidents' },
    { label: 'Report Hazard', icon: AlertTriangle, path: '/quick-hazard', color: 'bg-amber-500', desc: 'Identify safety hazards' },
    { label: 'Near Miss', icon: AlertCircle, path: '/near-miss', color: 'bg-orange-500', desc: 'Report close calls' },
    { label: 'New JHA', icon: FileText, path: '/jha-builder', color: 'bg-blue-500', desc: 'Create job hazard analysis' },
    { label: 'AI Photo Scan', icon: Camera, path: '/ai-hazard-scan', color: 'bg-purple-500', desc: 'Scan for hazards with AI' },
    { label: 'Create Permit', icon: Lock, path: '/permits', color: 'bg-orange-600', desc: 'Work permit request' },
  ];

  // Main feature categories
  const featureCategories = [
    {
      title: 'Safety Documentation',
      description: 'Create and manage safety documents',
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      features: [
        { name: 'JHA/JSA Builder', desc: 'Job hazard analysis', icon: FileText, path: '/jha-builder', badge: 'NEW', stat: stats.jhas },
        { name: 'Permit to Work', desc: 'Work permits', icon: Lock, path: '/permits', badge: 'NEW', stat: stats.permits },
        { name: 'Daily Pre-Task Plan', desc: 'Daily safety plans', icon: Clipboard, path: '/daily-ptp' },
        { name: 'OSHA Logs', desc: '300/300A/301 forms', icon: ClipboardList, path: '/osha-logs' },
      ]
    },
    {
      title: 'Incident Management',
      description: 'Track and investigate safety events',
      icon: Siren,
      color: 'from-red-500 to-red-600',
      features: [
        { name: 'Incidents', desc: 'Report & track', icon: Siren, path: '/incidents', stat: stats.incidents },
        { name: 'Near Misses', desc: 'Close call reports', icon: AlertCircle, path: '/near-miss', stat: stats.nearMisses },
        { name: 'Investigations', desc: 'Root cause analysis', icon: FileSearch, path: '/incident-investigations', badge: 'NEW', stat: stats.investigations },
        { name: 'Quick Hazard', desc: 'Fast hazard reports', icon: AlertTriangle, path: '/quick-hazard', stat: stats.hazards },
      ]
    },
    {
      title: 'Inspections & Observations',
      description: 'Monitor workplace safety',
      icon: Eye,
      color: 'from-green-500 to-green-600',
      features: [
        { name: 'Observations', desc: 'Safety observations', icon: Eye, path: '/observations', stat: stats.observations },
        { name: 'Equipment Log', desc: 'Inspection records', icon: Wrench, path: '/equipment-log' },
        { name: 'Safety Scorecard', desc: 'Performance metrics', icon: Target, path: '/safety-scorecard' },
        { name: 'Suggestion Box', desc: 'Improvement ideas', icon: MessageSquare, path: '/suggestion-box' },
      ]
    },
    {
      title: 'Training & Compliance',
      description: 'Manage certifications and training',
      icon: GraduationCap,
      color: 'from-purple-500 to-purple-600',
      features: [
        { name: 'Training Management', desc: 'Track certifications', icon: GraduationCap, path: '/training-management', badge: 'NEW', stat: stats.training },
        { name: 'Training Center', desc: 'OSHA courses', icon: BookOpen, path: '/training' },
        { name: 'Toolbox Talks', desc: 'Safety meetings', icon: Users, path: '/toolbox-talks' },
        { name: 'Achievements', desc: 'Points & rewards', icon: Award, path: '/gamification' },
      ]
    },
    {
      title: 'Analytics & Insights',
      description: 'Data-driven safety decisions',
      icon: BarChart3,
      color: 'from-indigo-500 to-indigo-600',
      features: [
        { name: 'Safety Analytics', desc: 'Risk scoring & trends', icon: BarChart3, path: '/safety-analytics', badge: 'NEW' },
        { name: 'ROI Dashboard', desc: 'Program value', icon: DollarSign, path: '/roi' },
        { name: 'Predictive Analytics', desc: 'Risk forecasting', icon: TrendingUp, path: '/analytics-intelligence' },
        { name: 'Safety Calendar', desc: 'Events & deadlines', icon: Calendar, path: '/safety-calendar' },
      ]
    },
    {
      title: 'Resources & Management',
      description: 'Manage contractors, chemicals & more',
      icon: Settings,
      color: 'from-slate-500 to-slate-600',
      features: [
        { name: 'Contractors', desc: 'Vendor compliance', icon: Truck, path: '/contractors' },
        { name: 'Chemical Inventory', desc: 'SDS management', icon: FlaskConical, path: '/chemical-inventory' },
        { name: 'Knowledge Center', desc: 'OSHA resources', icon: Brain, path: '/knowledge-center' },
        { name: 'Translation', desc: 'Multi-language', icon: Languages, path: '/translation' },
      ]
    },
  ];

  // Key metrics for the top bar
  const keyMetrics = [
    { label: 'Incidents', value: stats.incidents || 0, icon: Siren, color: 'text-red-500', path: '/incidents' },
    { label: 'Near Misses', value: stats.nearMisses || 0, icon: AlertCircle, color: 'text-orange-500', path: '/near-miss' },
    { label: 'Hazards', value: stats.hazards || 0, icon: AlertTriangle, color: 'text-amber-500', path: '/quick-hazard' },
    { label: 'JHAs', value: stats.jhas || 0, icon: FileText, color: 'text-blue-500', path: '/jha-builder' },
    { label: 'Permits', value: stats.permits || 0, icon: Lock, color: 'text-orange-600', path: '/permits' },
    { label: 'Observations', value: stats.observations || 0, icon: Eye, color: 'text-green-500', path: '/observations' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50">
      {/* Onboarding Wizard for new users */}
      {showOnboarding && (
        <OnboardingWizard 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white -mx-3 md:-mx-6 -mt-3 md:-mt-6 px-4 md:px-6">
        <div className="max-w-7xl mx-auto py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Safety Dashboard</h1>
                <p className="text-blue-100 text-sm">Your complete safety management hub</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20"
                onClick={fetchStats}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                size="sm" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/safety-analytics')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">View Analytics</span>
                <span className="sm:hidden">Analytics</span>
              </Button>
            </div>
          </div>

          {/* Key Metrics Bar */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-6">
            {keyMetrics.map((metric, idx) => (
              <div 
                key={idx}
                onClick={() => navigate(metric.path)}
                className="bg-white/10 backdrop-blur rounded-lg p-3 cursor-pointer hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <metric.icon className="w-5 h-5 text-white/80" />
                  <span className="text-2xl font-bold">{metric.value}</span>
                </div>
                <p className="text-xs text-blue-100 mt-1">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        
        {/* AI Hazard Scan Hero Section */}
        <section className="relative overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-800 rounded-xl p-4 text-white">
            <div className="relative flex flex-col md:flex-row items-center gap-4">
              {/* Icon and Content */}
              <div className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold">AI Hazard Scan</h2>
                    <Badge className="bg-amber-400 text-amber-900 text-xs font-semibold">POWERED BY AI</Badge>
                  </div>
                  <p className="text-purple-200 text-sm mt-0.5">
                    Upload photos for instant AI-powered hazard detection & OSHA compliance analysis
                  </p>
                </div>
              </div>
              
              {/* Stats - Inline */}
              <div className="flex items-center gap-3 text-center">
                <div className="px-3 py-1.5 bg-white/10 rounded-lg">
                  <div className="text-lg font-bold">{stats.aiScanTotal || 0}</div>
                  <div className="text-[10px] text-purple-200">Scans</div>
                </div>
                <div className="px-3 py-1.5 bg-white/10 rounded-lg">
                  <div className="text-lg font-bold">{stats.aiScanHazards || 0}</div>
                  <div className="text-[10px] text-purple-200">Hazards</div>
                </div>
                <div className="px-3 py-1.5 bg-white/10 rounded-lg">
                  <div className="text-lg font-bold text-green-300">{stats.aiScanClean || 100}%</div>
                  <div className="text-[10px] text-purple-200">Clean</div>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                <Button 
                  size="sm"
                  className="bg-white text-purple-700 hover:bg-purple-50 font-semibold shadow-lg"
                  onClick={() => navigate('/ai-hazard-scan')}
                  data-testid="ai-scan-hero-btn"
                >
                  <Camera className="w-4 h-4 mr-1.5" />
                  Start Scan
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate('/cctv-monitoring')}
                >
                  <Activity className="w-4 h-4 mr-1.5" />
                  CCTV
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-800">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickActions.map((action, idx) => (
              <Card 
                key={idx}
                className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 group bg-white border border-slate-200"
                onClick={() => navigate(action.path)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-medium text-slate-800 text-sm">{action.label}</h3>
                  <p className="text-xs text-slate-500 mt-1">{action.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Quick Export Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-slate-800">Quick Export</h2>
            <span className="text-xs text-slate-500">Download reports instantly</span>
          </div>
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <QuickExportSection />
            </CardContent>
          </Card>
        </section>

        {/* Feature Categories */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Home className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-slate-800">All Features</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCategories.map((category, catIdx) => (
              <Card key={catIdx} className="overflow-hidden bg-white border border-slate-200">
                <div className={`bg-gradient-to-r ${category.color} p-4`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{category.title}</h3>
                      <p className="text-xs text-white/80">{category.description}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-0">
                  {category.features.map((feature, featIdx) => (
                    <div
                      key={featIdx}
                      onClick={() => navigate(feature.path)}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 transition-colors group"
                    >
                      <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                        <feature.icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 text-sm">{feature.name}</span>
                          {feature.badge && (
                            <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">{feature.badge}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{feature.desc}</p>
                      </div>
                      {feature.stat !== undefined && (
                        <span className="text-lg font-bold text-slate-700">{feature.stat}</span>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* AI Features Banner */}
        <section>
          <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">AI-Powered Safety</h3>
                    <p className="text-purple-100 text-sm mt-1">
                      Use artificial intelligence to identify hazards, analyze risks, and improve safety
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex gap-3">
                  <Button 
                    variant="secondary" 
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={() => navigate('/ai-hazard-scan')}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Photo Scan
                  </Button>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={() => navigate('/knowledge-center')}
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    AI Assistant
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Help Section */}
        <section className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/help')}>
                <HelpCircle className="w-4 h-4 mr-2" />
                Help Center
              </Button>
              <Button variant="outline" onClick={() => navigate('/support')}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </div>
            <p className="text-sm text-slate-500">
              OSHA Compliant Safety Management System
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
